import { Phone, Mail } from "lucide-react";
import { Eyebrow, Reveal } from "./bits";

const platformParagraphs = [
  "HRKAPITAL operates as a Multi-Family Investment Office providing access to a curated platform of financial market services for its clients. The firm is not registered as a SEBI Registered Investment Advisor (RIA) and therefore does not provide investment advisory services under the SEBI (Investment Advisers) Regulations, 2013.",
  "HRKAPITAL facilitates client access to various financial products and services by aggregating best-in-class financial institutions across multiple verticals, including Mutual Funds, Portfolio Management Services (PMS), Alternative Investment Funds (AIF), structured products, and other investment solutions, through distribution and referral arrangements.",
  "For investments executed by clients through the HRKAPITAL platform, the firm may act as an AMFI Registered Mutual Fund Distributor (MFD) or as a distribution/referral partner for other financial service providers.",
  "HRKAPITAL receives distribution commissions or referral fees from the respective Mutual Fund houses, PMS providers, AIFs, or other financial product providers for facilitating client access to these investment opportunities. The firm does not charge clients directly for such distribution services, unless otherwise explicitly agreed.",
  "The compensation received by HRKAPITAL forms part of the commercial understanding between the client families and HRKAPITAL for availing the platform's services, and such arrangements are disclosed in accordance with applicable regulations and industry practices.",
  "HRKAPITAL is registered with the Association of Mutual Funds in India (AMFI) as a Mutual Fund Distributor (MFD) under ARN Code – 369251.",
];

const regulatoryParagraphs = [
  "Investments in Mutual Funds, securities, PMS, AIFs, and other financial products are subject to market risks. The value of investments may fluctuate depending on market conditions, and past performance does not guarantee future returns.",
  "Investors are advised to carefully read all scheme-related documents, offer documents, and risk factors before making any investment decision. Investment decisions should be based on the investor's financial objectives, risk tolerance, and investment horizon.",
  "HRKAPITAL acts only as a facilitator / distributor and does not guarantee returns or performance of any investment product.",
];

const conflictParagraphs = [
  "As a distributor and platform provider, HRKAPITAL may receive commissions, trail fees, or referral fees from product manufacturers or service providers whose products are offered through the platform.",
  "Accordingly, there may be situations where HRKAPITAL or its affiliates have a financial interest in the distribution of certain products or services. However, the firm endeavours to maintain transparency, integrity, and a client-first approach while facilitating access to investment opportunities.",
  "Clients are encouraged to evaluate product documentation and consult their professional advisors before making investment decisions.",
];

const Card = ({ title, paragraphs, testId }) => (
  <div
    data-testid={testId}
    className="h-full rounded-3xl border border-[#10202E]/[0.08] bg-white p-8 shadow-[0_2px_24px_rgba(16,32,46,0.05)] sm:p-10"
  >
    <h3 className="font-display text-2xl text-[#10202E]">{title}</h3>
    <div className="mt-5 space-y-4">
      {paragraphs.map((p, i) => (
        <p key={i} className="text-sm font-light leading-relaxed text-[#4E6A82]">
          {p}
        </p>
      ))}
    </div>
  </div>
);

export const Disclosure = () => (
  <section
    id="disclosure"
    data-testid="disclosure-section"
    className="relative border-y border-[#10202E]/[0.06] bg-[#E8EFF4]/60 py-28 md:py-40"
  >
    <div className="mx-auto max-w-6xl px-6">
      <Reveal>
        <Eyebrow>10 — Key Terms & Disclosure</Eyebrow>
      </Reveal>
      <Reveal delay={0.08}>
        <h2 className="mt-6 max-w-3xl font-display text-4xl leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
          Clear on paper, <span className="italic text-emerald-600">too.</span>
        </h2>
      </Reveal>
      <Reveal delay={0.14}>
        <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-[#4E6A82]">
          Exactly how HRKAPITAL works, how we earn, and what you should know — stated plainly, the
          way it should be.
        </p>
      </Reveal>

      <div className="mt-16 space-y-5">
        <Reveal>
          <div
            data-testid="disclosure-platform-card"
            className="rounded-3xl border border-[#10202E]/[0.08] bg-white p-8 shadow-[0_2px_24px_rgba(16,32,46,0.05)] sm:p-10"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <h3 className="font-display text-2xl text-[#10202E]">Our platform & how we earn</h3>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-700">
                ARN – 369251
              </span>
            </div>
            <div className="mt-5 grid gap-x-10 gap-y-4 md:grid-cols-2">
              {platformParagraphs.map((p, i) => (
                <p key={i} className="text-sm font-light leading-relaxed text-[#4E6A82]">
                  {p}
                </p>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="grid gap-5 md:grid-cols-2">
          <Reveal delay={0.05} className="h-full">
            <Card title="Regulatory Disclaimer" paragraphs={regulatoryParagraphs} testId="disclosure-regulatory-card" />
          </Reveal>
          <Reveal delay={0.1} className="h-full">
            <Card title="Conflict of Interest Disclosure" paragraphs={conflictParagraphs} testId="disclosure-conflict-card" />
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div
            data-testid="disclosure-grievance"
            className="flex flex-wrap items-center justify-between gap-6 rounded-3xl border border-emerald-600/20 bg-[#DDF7EC] p-8 sm:p-10"
          >
            <p className="max-w-xl font-display text-2xl leading-snug text-[#10202E]">
              In case of a grievance, we want to hear from you — <span className="italic text-emerald-700">directly.</span>
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="tel:+916202743226"
                data-testid="grievance-phone-link"
                className="inline-flex items-center gap-2.5 rounded-full border border-[#10202E]/15 bg-white/70 px-5 py-3 text-sm font-medium text-[#10202E] transition-colors duration-300 hover:border-emerald-600/50"
              >
                <Phone className="h-4 w-4 text-emerald-600" />
                62027 43226
              </a>
              <a
                href="mailto:services@hrkapital.co.in"
                data-testid="grievance-email-link"
                className="inline-flex items-center gap-2.5 rounded-full border border-[#10202E]/15 bg-white/70 px-5 py-3 text-sm font-medium text-[#10202E] transition-colors duration-300 hover:border-emerald-600/50"
              >
                <Mail className="h-4 w-4 text-emerald-600" />
                services@hrkapital.co.in
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
